import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import {
  getCheckListTopicsWithItems,
  CheckListTopic,
  CheckListItem,
  createCheckListTopic,
  updateCheckListTopic,
  deleteCheckListTopic,
  createCheckListItem,
  updateCheckListItem,
  deleteCheckListItem,
} from '../../services/checkListService';

const CheckListConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const [topics, setTopics] = useState<CheckListTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Topic Modal State
  const [topicModal, setTopicModal] = useState(false);
  const [topicMode, setTopicMode] = useState<'create' | 'edit'>('create');
  const [currentTopic, setCurrentTopic] = useState<CheckListTopic | null>(null);
  const [topicName, setTopicName] = useState('');
  const [topicDisplayOrder, setTopicDisplayOrder] = useState(1);
  const [isSavingTopic, setIsSavingTopic] = useState(false);

  // Item Modal State
  const [itemModal, setItemModal] = useState(false);
  const [itemMode, setItemMode] = useState<'create' | 'edit'>('create');
  const [currentItem, setCurrentItem] = useState<CheckListItem | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState('');
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemResponsible, setItemResponsible] = useState('');
  const [itemDeadlineValue, setItemDeadlineValue] = useState(1);
  const [itemDeadlineUnit, setItemDeadlineUnit] = useState('Dia(s)');
  const [itemObservation, setItemObservation] = useState('');
  const [itemDisplayOrder, setItemDisplayOrder] = useState(1);
  const [itemSkiped, setItemSkiped] = useState(false);
  const [itemDependencyIds, setItemDependencyIds] = useState<string[]>([]);
  const [isSavingItem, setIsSavingItem] = useState(false);

  const loadTopics = async () => {
    try {
      setIsLoading(true);
      const data = await getCheckListTopicsWithItems();
      setTopics(data);
    } catch (error: any) {
      toast.error(error.message || t('Erro ao carregar configuração do checklist'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== TOPIC CRUD =====

  const openCreateTopicModal = () => {
    setTopicMode('create');
    setCurrentTopic(null);
    setTopicName('');
    setTopicDisplayOrder(topics.length + 1);
    setTopicModal(true);
  };

  const openEditTopicModal = (topic: CheckListTopic) => {
    setTopicMode('edit');
    setCurrentTopic(topic);
    setTopicName(topic.name);
    setTopicDisplayOrder(topic.displayOrder);
    setTopicModal(true);
  };

  const handleSaveTopic = async () => {
    if (!topicName.trim()) {
      toast.error(t('Nome do tópico é obrigatório'));
      return;
    }

    try {
      setIsSavingTopic(true);
      if (topicMode === 'create') {
        await createCheckListTopic({
          name: topicName,
          displayOrder: topicDisplayOrder,
        });
        toast.success(t('Tópico criado com sucesso'));
      } else if (currentTopic) {
        await updateCheckListTopic(currentTopic.id, {
          name: topicName,
          displayOrder: topicDisplayOrder,
        });
        toast.success(t('Tópico atualizado com sucesso'));
      }
      setTopicModal(false);
      loadTopics();
    } catch (error: any) {
      toast.error(error.message || t('Erro ao salvar tópico'));
    } finally {
      setIsSavingTopic(false);
    }
  };

  const handleDeleteTopic = async (topicId: string, topicName: string) => {
    if (!window.confirm(t(`Deseja realmente excluir o tópico "${topicName}"?`))) {
      return;
    }

    try {
      await deleteCheckListTopic(topicId);
      toast.success(t('Tópico excluído com sucesso'));
      loadTopics();
    } catch (error: any) {
      toast.error(error.message || t('Erro ao excluir tópico'));
    }
  };

  // ===== ITEM CRUD =====

  const openCreateItemModal = (topicId: string) => {
    const topic = topics.find(t => t.id === topicId);
    const itemCount = topic?.items.length || 0;

    setItemMode('create');
    setCurrentItem(null);
    setSelectedTopicId(topicId);
    setItemName('');
    setItemDescription('');
    setItemResponsible('');
    setItemDeadlineValue(1);
    setItemDeadlineUnit('Dia(s)');
    setItemObservation('');
    setItemDisplayOrder(itemCount + 1);
    setItemSkiped(false);
    setItemDependencyIds([]);
    setItemModal(true);
  };

  const openEditItemModal = (item: CheckListItem) => {
    setItemMode('edit');
    setCurrentItem(item);
    setSelectedTopicId(item.topicId);
    setItemName(item.name);
    setItemDescription(item.description || '');
    setItemResponsible(item.responsible);
    setItemDeadlineValue(item.deadlineValue);
    setItemDeadlineUnit(item.deadlineUnit);
    setItemObservation(item.observation || '');
    setItemDisplayOrder(item.displayOrder);
    setItemSkiped(item.skiped || false);
    setItemDependencyIds(item.dependencyItemIds || []);
    setItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!itemName.trim()) {
      toast.error(t('Nome do item é obrigatório'));
      return;
    }
    if (!itemResponsible.trim()) {
      toast.error(t('Responsável é obrigatório'));
      return;
    }
    // Validation: if Skiped is checked, Observation must have at least 15 characters
    if (itemSkiped && (!itemObservation || itemObservation.trim().length < 15)) {
      toast.error(t('Observação deve ter no mínimo 15 caracteres quando o item está marcado como Skiped'));
      return;
    }

    try {
      setIsSavingItem(true);
      const itemData = {
        topicId: selectedTopicId,
        name: itemName,
        description: itemDescription || undefined,
        responsible: itemResponsible,
        deadlineValue: itemDeadlineValue,
        deadlineUnit: itemDeadlineUnit,
        observation: itemObservation || undefined,
        displayOrder: itemDisplayOrder,
        skiped: itemSkiped,
        dependencyItemIds: itemDependencyIds.length > 0 ? itemDependencyIds : undefined,
      };

      if (itemMode === 'create') {
        await createCheckListItem(itemData);
        toast.success(t('Item criado com sucesso'));
      } else if (currentItem) {
        await updateCheckListItem(currentItem.id, itemData);
        toast.success(t('Item atualizado com sucesso'));
      }
      setItemModal(false);
      loadTopics();
    } catch (error: any) {
      toast.error(error.message || t('Erro ao salvar item'));
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId: string, itemName: string) => {
    if (!window.confirm(t(`Deseja realmente excluir o item "${itemName}"?`))) {
      return;
    }

    try {
      await deleteCheckListItem(itemId);
      toast.success(t('Item excluído com sucesso'));
      loadTopics();
    } catch (error: any) {
      toast.error(error.message || t('Erro ao excluir item'));
    }
  };

  const getTotalItems = () => {
    return topics.reduce((sum, topic) => sum + topic.items.length, 0);
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('Configuração do Check List')} pageTitle={t('Administração')} />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{t('Carregando...')}</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t('Configuração do Check List')} pageTitle={t('Administração')} />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="card-title mb-0">
                    {t('Check List de Abertura de Empresas')}
                  </h5>
                  <p className="text-muted mt-1 mb-0">
                    {t('Gerencie os tópicos e itens do checklist')}
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="text-end me-3">
                    <div className="text-muted small">
                      {topics.length} {t('tópicos')} • {getTotalItems()} {t('itens')}
                    </div>
                  </div>
                  <Button color="primary" onClick={openCreateTopicModal}>
                    <i className="ri-add-line me-1"></i>
                    {t('Novo Tópico')}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {topics.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="ri-file-list-line text-muted fs-1"></i>
                    <p className="text-muted mt-2">
                      {t('Nenhum tópico configurado. Clique em "Novo Tópico" para começar.')}
                    </p>
                  </div>
                ) : (
                  <div className="accordion" id="checklistConfigAccordion">
                    {topics
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((topic) => (
                        <Card key={topic.id} className="mb-3 border">
                          <CardHeader className="bg-light">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-0">
                                  <Badge color="primary" className="me-2">
                                    {topic.displayOrder}
                                  </Badge>
                                  {topic.name}
                                </h6>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Badge color="secondary" pill>
                                  {topic.items.length} {t('itens')}
                                </Badge>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => openCreateItemModal(topic.id)}
                                >
                                  <i className="ri-add-line"></i>
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => openEditTopicModal(topic)}
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteTopic(topic.id, topic.name)}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardBody className="p-0">
                            {topic.items.length === 0 ? (
                              <div className="p-3 text-muted text-center">
                                {t('Nenhum item neste tópico')}
                              </div>
                            ) : (
                              <Table className="table-borderless mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th>{t('Item')}</th>
                                    <th style={{ width: '150px' }}>{t('Responsável')}</th>
                                    <th style={{ width: '120px' }}>{t('Prazo')}</th>
                                    <th style={{ width: '100px' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {topic.items
                                    .sort((a, b) => a.displayOrder - b.displayOrder)
                                    .map((item) => (
                                      <tr key={item.id}>
                                        <td>
                                          <Badge color="light" className="text-dark">
                                            {item.displayOrder}
                                          </Badge>
                                        </td>
                                        <td>
                                          <div>
                                            <div className="d-flex align-items-center gap-2">
                                              <strong>{item.name}</strong>
                                              {item.skiped && (
                                                <Badge color="warning" className="bg-warning-subtle text-warning" pill>
                                                  <i className="ri-skip-forward-line me-1"></i>
                                                  Skiped
                                                </Badge>
                                              )}
                                              {item.dependencyItemIds && item.dependencyItemIds.length > 0 && (
                                                <Badge color="info" className="bg-info-subtle text-info" pill>
                                                  <i className="ri-link me-1"></i>
                                                  {item.dependencyItemIds.length} dep.
                                                </Badge>
                                              )}
                                            </div>
                                            {item.description && (
                                              <p className="text-muted mb-0 small">
                                                {item.description}
                                              </p>
                                            )}
                                            {item.dependencyItemIds && item.dependencyItemIds.length > 0 && (
                                              <div className="mt-1">
                                                <small className="text-info">
                                                  <i className="ri-arrow-right-line me-1"></i>
                                                  <strong>Condição:</strong> Aguarda conclusão de {
                                                    item.dependencyItemIds.map(depId => {
                                                      const depItem = topics
                                                        .flatMap(t => t.items)
                                                        .find(i => i.id === depId);
                                                      return depItem?.name || 'Item desconhecido';
                                                    }).join(', ')
                                                  }
                                                </small>
                                              </div>
                                            )}
                                            {item.observation && (
                                              <div className="mt-1">
                                                <small className="text-info">
                                                  <i className="ri-information-line me-1"></i>
                                                  {item.observation}
                                                </small>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <Badge color="primary" className="bg-primary-subtle text-primary">
                                            {item.responsible}
                                          </Badge>
                                        </td>
                                        <td>
                                          <span className="text-muted">
                                            {item.deadlineValue} {item.deadlineUnit}
                                          </span>
                                        </td>
                                        <td>
                                          <div className="d-flex gap-1">
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => openEditItemModal(item)}
                                            >
                                              <i className="ri-edit-line"></i>
                                            </Button>
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => handleDeleteItem(item.id, item.name)}
                                            >
                                              <i className="ri-delete-bin-line"></i>
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </Table>
                            )}
                          </CardBody>
                        </Card>
                      ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Topic Modal */}
        <Modal isOpen={topicModal} toggle={() => setTopicModal(false)}>
          <ModalHeader toggle={() => setTopicModal(false)}>
            {topicMode === 'create' ? t('Novo Tópico') : t('Editar Tópico')}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="topicName">{t('Nome do Tópico')} *</Label>
                <Input
                  id="topicName"
                  type="text"
                  value={topicName}
                  onChange={(e) => setTopicName(e.target.value)}
                  placeholder={t('Ex: Documentação Inicial')}
                />
              </FormGroup>
              <FormGroup>
                <Label for="topicDisplayOrder">{t('Ordem de Exibição')} *</Label>
                <Input
                  id="topicDisplayOrder"
                  type="number"
                  min="1"
                  value={topicDisplayOrder}
                  onChange={(e) => setTopicDisplayOrder(parseInt(e.target.value) || 1)}
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setTopicModal(false)} disabled={isSavingTopic}>
              {t('Cancelar')}
            </Button>
            <Button color="primary" onClick={handleSaveTopic} disabled={isSavingTopic}>
              {isSavingTopic ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('Salvando...')}
                </>
              ) : (
                t('Salvar')
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Item Modal */}
        <Modal isOpen={itemModal} toggle={() => setItemModal(false)} size="lg">
          <ModalHeader toggle={() => setItemModal(false)}>
            {itemMode === 'create' ? t('Novo Item') : t('Editar Item')}
          </ModalHeader>
          <ModalBody>
            <Form>
              <Row>
                <Col md={8}>
                  <FormGroup>
                    <Label for="itemName">{t('Nome do Item')} *</Label>
                    <Input
                      id="itemName"
                      type="text"
                      value={itemName}
                      onChange={(e) => setItemName(e.target.value)}
                      placeholder={t('Ex: Cópia do Contrato Social')}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label for="itemDisplayOrder">{t('Ordem')} *</Label>
                    <Input
                      id="itemDisplayOrder"
                      type="number"
                      min="1"
                      value={itemDisplayOrder}
                      onChange={(e) => setItemDisplayOrder(parseInt(e.target.value) || 1)}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="itemDescription">{t('Descrição')}</Label>
                <Input
                  id="itemDescription"
                  type="textarea"
                  rows={2}
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  placeholder={t('Descrição detalhada do item (opcional)')}
                />
              </FormGroup>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="itemResponsible">{t('Responsável')} *</Label>
                    <Input
                      id="itemResponsible"
                      type="select"
                      value={itemResponsible}
                      onChange={(e) => setItemResponsible(e.target.value)}
                    >
                      <option value="">{t('Selecione...')}</option>
                      <option value="Integração">Integração</option>
                      <option value="Legalização">Legalização</option>
                      <option value="Fiscal">Fiscal</option>
                      <option value="Atendimento">Atendimento</option>
                      <option value="Cliente">Cliente</option>
                      <option value="Contador">Contador</option>
                      <option value="Advogado">Advogado</option>
                      <option value="Ambos">Ambos</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="itemDeadlineValue">{t('Prazo')} *</Label>
                    <Input
                      id="itemDeadlineValue"
                      type="number"
                      min="1"
                      value={itemDeadlineValue}
                      onChange={(e) => setItemDeadlineValue(parseInt(e.target.value) || 1)}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label for="itemDeadlineUnit">{t('Unidade')} *</Label>
                    <Input
                      id="itemDeadlineUnit"
                      type="select"
                      value={itemDeadlineUnit}
                      onChange={(e) => setItemDeadlineUnit(e.target.value)}
                    >
                      <option value="Hora(s)">Hora(s)</option>
                      <option value="Dia(s)">Dia(s)</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="itemObservation">
                  {t('Observação')}
                  {itemSkiped && <span className="text-danger"> * (mínimo 15 caracteres)</span>}
                </Label>
                <Input
                  id="itemObservation"
                  type="textarea"
                  rows={2}
                  value={itemObservation}
                  onChange={(e) => setItemObservation(e.target.value)}
                  placeholder={itemSkiped ? t('Observação obrigatória com no mínimo 15 caracteres') : t('Observações adicionais (opcional)')}
                  className={itemSkiped && (!itemObservation || itemObservation.length < 15) ? 'border-danger' : ''}
                />
                {itemSkiped && itemObservation && (
                  <small className={itemObservation.length >= 15 ? 'text-success' : 'text-danger'}>
                    {itemObservation.length}/15 caracteres
                  </small>
                )}
              </FormGroup>

              <FormGroup check className="mb-3">
                <Input
                  id="itemSkiped"
                  type="checkbox"
                  checked={itemSkiped}
                  onChange={(e) => setItemSkiped(e.target.checked)}
                />
                <Label check for="itemSkiped">
                  <strong>{t('Item Skiped (Pulado)')}</strong>
                  <br />
                  <small className="text-muted">
                    {t('Marque esta opção para permitir pular este item. A Observação se torna obrigatória com no mínimo 15 caracteres.')}
                  </small>
                </Label>
              </FormGroup>

              <FormGroup>
                <Label for="itemDependencies">
                  {t('Condição (Dependências)')}
                  <br />
                  <small className="text-muted">
                    {t('Selecione os itens que devem ser concluídos antes deste poder ser iniciado')}
                  </small>
                </Label>
                <Input
                  id="itemDependencies"
                  type="select"
                  multiple
                  value={itemDependencyIds}
                  onChange={(e) => {
                    const selectElement = e.target as unknown as HTMLSelectElement;
                    const selected = Array.from(selectElement.selectedOptions, option => option.value);
                    setItemDependencyIds(selected);
                  }}
                  style={{ minHeight: '120px' }}
                >
                  {topics
                    .flatMap(topic =>
                      topic.items
                        .filter(item => item.id !== currentItem?.id) // Don't allow self-reference
                        .map(item => ({
                          topicName: topic.name,
                          ...item
                        }))
                    )
                    .sort((a, b) => {
                      const topicCompare = a.topicName.localeCompare(b.topicName);
                      if (topicCompare !== 0) return topicCompare;
                      return a.displayOrder - b.displayOrder;
                    })
                    .map(item => (
                      <option key={item.id} value={item.id}>
                        [{item.topicName}] {item.displayOrder}. {item.name}
                      </option>
                    ))
                  }
                </Input>
                {itemDependencyIds.length > 0 && (
                  <small className="text-info">
                    <i className="ri-information-line me-1"></i>
                    {itemDependencyIds.length} {t('dependência(s) selecionada(s)')}
                  </small>
                )}
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setItemModal(false)} disabled={isSavingItem}>
              {t('Cancelar')}
            </Button>
            <Button color="primary" onClick={handleSaveItem} disabled={isSavingItem}>
              {isSavingItem ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('Salvando...')}
                </>
              ) : (
                t('Salvar')
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default CheckListConfiguration;
